import axios from 'axios';
import axiosInstance from './axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URI;

// Validate API URL
if (!API_BASE_URL) {
  console.error('API_BASE_URL is not defined. Please check your environment variables.');
  throw new Error('API_BASE_URL is not defined');
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get footer links by categories
export const getHeaderLinks = async () => {
  try {
    const [services] = await Promise.all([
      axiosInstance.get(`${API_BASE_URL}/api/content-pages/category/services`)
    ]);

    return {
      serviceLinks: services.data.map(page => ({
        name: page.title,
        path: `/${page.category}/${page.slug}`
      }))
    };
  } catch (error) {
    console.error('Error fetching header links:', error);
    throw error;
  }
};

// Get footer links by categories
export const getFooterLinks = async () => {
  try {
    const [legal, support, services] = await Promise.all([
      axiosInstance.get(`${API_BASE_URL}/api/content-pages/category/legal`),
      axiosInstance.get(`${API_BASE_URL}/api/content-pages/category/support`),
      axiosInstance.get(`${API_BASE_URL}/api/content-pages/category/services`)
    ]);

    return {
      legalLinks: legal.data.map(page => ({
        name: page.title,
        path: `/${page.category}/${page.slug}`
      })),
      supportLinks: support.data.map(page => ({
        name: page.title,
        path: `/${page.category}/${page.slug}`
      })),
      serviceLinks: services.data.map(page => ({
        name: page.title,
        path: `/${page.category}/${page.slug}`
      }))
    };
  } catch (error) {
    console.error('Error fetching footer links:', error);
    throw error;
  }
};

// Get all content pages (admin)
export const getAllContentPages = async (filters = {}) => {
  try {
    const queryString = new URLSearchParams(filters).toString();
    const response = await axiosInstance.get(
      `${API_BASE_URL}/api/content-pages${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching content pages:', error);
    throw error;
  }
};

// Get page by ID (admin)
export const getContentPageById = async (id) => {
  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}/api/content-pages/${id}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching page:', error);
    throw error;
  }
};

// Get page by slug (public)
export const getContentPageBySlug = async (slug, category = null) => {
  try {
    if (!slug) {
      throw new Error('Slug is required');
    }

    const queryString = category ? `?category=${category}` : '';
    const response = await axiosInstance.get(`${API_BASE_URL}/api/content-pages/slug/${slug}${queryString}`);
    
    if (!response.data) {
      throw new Error('Page not found');
    }

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Page not found');
    }
    throw error;
  }
};

// Get pages by category (public)
export const getContentPagesByCategory = async (category) => {
  try {
    if (!category) {
      throw new Error('Category is required');
    }

    const response = await axiosInstance.get(`${API_BASE_URL}/api/content-pages/category/${category}`);
    
    if (!response.data) {
      throw new Error('No pages found');
    }

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('No pages found');
    }
    throw error;
  }
};

// Create new content page (admin)
export const createContentPage = async (pageData) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/api/content-pages`,
      pageData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating content page:', error);
    throw error;
  }
};

// Update content page (admin)
export const updateContentPage = async (id, pageData) => {
  try {
    const response = await axiosInstance.put(
      `${API_BASE_URL}/api/content-pages/${id}`,
      pageData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating content page:', error);
    throw error;
  }
};

// Delete content page (admin)
export const deleteContentPage = async (id) => {
  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}/api/content-pages/${id}`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting content page:', error);
    throw error;
  }
};

// Update page status (admin)
export const updateContentPageStatus = async (id, status) => {
  try {
    const response = await axiosInstance.patch(
      `${API_BASE_URL}/api/content-pages/${id}/status`,
      { status }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating page status:', error);
    throw error;
  }
};

// Update pages order (admin)
export const updateContentPagesOrder = async (pages) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/api/content-pages/reorder`,
      { pages }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating pages order:', error);
    throw error;
  }
};

